"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var mutex_1 = require("./mutex");
/**
 * Base dependency graph class.
 */
var Graph = /** @class */ (function () {
    function Graph() {
        this.nodes = {};
        this.outgoingEdges = {};
        this.incomingEdges = {};
    }
    /**
     * Add a node to the graph.
     * @param node Node object.
     */
    Graph.prototype.addNode = function (node) {
        if (this.hasNode(node.name))
            throw new Error("A node with the name of \"" + node.name + "\" already exists in the graph!");
        this.nodes[node.name] = node;
        this.outgoingEdges[node.name] = [];
        this.incomingEdges[node.name] = [];
    };
    /**
     * Remove a node by name from the graph.
     * @param name Node name.
     */
    Graph.prototype.removeNode = function (name) {
        if (!this.hasNode(name))
            throw new Error("A node with the name of \"" + name + "\" does not exist in the graph!");
        delete this.nodes[name];
        delete this.outgoingEdges[name];
        delete this.incomingEdges[name];
        for (var dependent in this.incomingEdges) {
            if (this.incomingEdges[dependent].includes(name))
                this.incomingEdges[dependent].splice(dependent.indexOf(name), 1);
        }
        for (var dependency in this.outgoingEdges) {
            if (this.outgoingEdges[dependency].includes(name))
                this.outgoingEdges[dependency].splice(dependency.indexOf(name), 1);
        }
    };
    /**
     * Checks to see if the graph contains a Node by name.
     * @param name Node name.
     */
    Graph.prototype.hasNode = function (name) {
        return this.nodes.hasOwnProperty(name);
    };
    Object.defineProperty(Graph.prototype, "size", {
        /**
         * Returns the number of nodes in a graph.
         */
        get: function () {
            return Object.keys(this.nodes).length;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns the Node instance given a node name.
     * @param name Node name.
     */
    Graph.prototype.getNode = function (name) {
        if (this.hasNode(name))
            return this.nodes[name];
        throw new Error("Node \"" + name + "\" not found!");
    };
    /**
     *  Adds a node dependence. "from" is dependent on "to"
     *  @param from Node name.
     *  @param to  Node name.
     */
    Graph.prototype.addDependency = function (from, to) {
        if (!this.hasNode(from))
            throw new Error("Node does not exist: " + from);
        if (!this.hasNode(to))
            throw new Error("Node does not exist: " + to);
        if (from === to)
            throw new Error("Cannot add self dependency: " + to);
        if (!this.outgoingEdges[from].includes(to)) {
            this.outgoingEdges[from].push(to);
        }
        if (!this.incomingEdges[to].includes(from)) {
            this.incomingEdges[to].push(from);
        }
    };
    /**
     * Removes a node dependence. "from" is no longer dependent on "to".
     * @param from Node name.
     * @param to  Node name.
     * @todo Test this function.
     */
    Graph.prototype.removeDependency = function (from, to) {
        console.warn('removeDependency() has not been tested.');
        if (!this.hasNode(from))
            throw new Error("Node does not exist: " + from);
        if (!this.hasNode(to))
            throw new Error("Node does not exist: " + to);
        if (from === to)
            throw new Error("Cannot remove self dependency: " + to);
        if (!this.outgoingEdges[from].includes(to)) {
            this.outgoingEdges[from].splice(this.outgoingEdges[from].indexOf(to), 1);
        }
        if (!this.incomingEdges[to].includes(from)) {
            this.incomingEdges[to].splice(this.incomingEdges[to].indexOf(to), 1);
        }
    };
    /**
     * Get dependency node names for a Node by name. (Required nodes for this node to execute).
     * @param name Node name.
     */
    Graph.prototype.dependenciesOf = function (name) {
        return this.outgoingEdges[name];
    };
    /**
     * Get dependents node names for a Node by name. (Nodes that require this node to complete).
     * @param name Node name.
     */
    Graph.prototype.dependentsOf = function (name) {
        return this.incomingEdges[name];
    };
    /**
     * Breadth first search.
     */
    Graph.prototype.traverse = function () {
        var _this = this;
        // Clear all complete node mutexes.
        Object.keys(this.nodes).map(function (name) { return _this.getNode(name).clearMutex(); });
        // Visiting a node recursively calls visit on each node's dependents.
        var visit = function (node) {
            // First await all dependencies
            return Promise.all(_this.dependenciesOf(node.name).map(function (dependencyName) { return _this.nodes[dependencyName].awaitData(); }))
                .then(function () {
                node.signalDependenciesReady();
                if (_this.dependentsOf(node.name).length > 0) {
                    // Then recursively visit all dependents
                    return Promise.all(_this.dependentsOf(node.name).map(function (dependentName) { return visit(_this.getNode(dependentName)); }));
                }
                else {
                    // node has no dependents so await data
                    return node.awaitData();
                }
            });
        };
        // Find nodes with no dependencies
        var rootNodeNames = Object.keys(this.nodes).filter(function (name) { return _this.dependenciesOf(name).length === 0; });
        if (rootNodeNames.length === 0 && Object.keys(this.nodes).length > 0)
            return Promise.reject(new Error('The graph is circular. Cannot traverse graph due to no root node.'));
        // Start recursive traversal from root nodes.
        return Promise.all(rootNodeNames.map(function (name) { return visit(_this.nodes[name]); }));
    };
    /**
     * Clears the value of a node and the values of dependent nodes
     * @param name Node name.
     */
    Graph.prototype.clearNodeAndDependents = function (name) {
        var _this = this;
        var node = this.getNode(name);
        var visitAndClear = function (node) {
            if (node.hasData()) {
                node.clearData();
                return Promise.all(_this.dependentsOf(node.name).map(function (dependentName) { return visitAndClear(_this.getNode(dependentName)); }));
            }
            else {
                return Promise.resolve();
            }
        };
        return visitAndClear(node);
    };
    /**
     * Resets the graph by resetting each node in the graph.
     */
    Graph.prototype.reset = function () {
        for (var name in this.nodes) {
            this.nodes[name].reset();
        }
    };
    /**
     * Prints graph nodes and node dependents.
     */
    Graph.prototype.ls = function () {
        for (var name in this.nodes) {
            console.log(name);
            console.log(this.dependentsOf(name));
        }
    };
    return Graph;
}());
exports.Graph = Graph;
var Node = /** @class */ (function () {
    function Node(name, promise) {
        this.locked = false;
        this._name = name;
        this._promise = promise;
    }
    Object.defineProperty(Node.prototype, "name", {
        get: function () {
            return this._name;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Await data.
     * @returns A `Promise<T | null>` that resolves when the node's data is ready.
     */
    Node.prototype.awaitData = function () {
        var _this = this;
        if (!this.mutex) {
            this.mutex = new mutex_1.Mutex(function () { return _this._data !== undefined; });
        }
        return new Promise(function (resolve, reject) {
            _this.mutex.await(function (error) {
                if (error)
                    return reject(error);
                resolve(_this._data);
            });
        });
    };
    Node.prototype.signalDependenciesReady = function () {
        var _this = this;
        if (!this.mutex) {
            this.mutex = new mutex_1.Mutex(function () { return _this._data !== undefined; });
        }
        if (this._data === undefined) {
            // Only allow to _promise once
            if (this.locked === true)
                return;
            this.locked = true;
            var promise = this._promise();
            if (promise === undefined)
                throw new Error("Node \"" + this._name + "\" has undefined promise.");
            promise.then(function (data) {
                _this._data = data;
                _this.locked = false;
                _this.mutex.ready();
            }).catch(function (error) {
                _this._data = null;
                _this.locked = false;
                _this.mutex.ready(error);
            });
        }
        else {
            // Allow _data to be set externally from promise
            this.mutex.ready();
        }
    };
    Node.prototype.setData = function (data) {
        this._data = data;
        this.signalDependenciesReady();
    };
    /**
     * Resets node. Clears all node data and resets its mutex.
     */
    Node.prototype.reset = function () {
        this.clearData();
        this.clearMutex();
    };
    Node.prototype.clearMutex = function () {
        if (this.hasData())
            this.mutex = undefined;
    };
    Node.prototype.hasData = function () {
        return this._data !== undefined;
    };
    Node.prototype.clearData = function () {
        this._data = undefined;
    };
    return Node;
}());
exports.Node = Node;
//# sourceMappingURL=data:application/json;base64,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